import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
    IonPage,
    IonContent,
    IonButton,
    IonIcon,
    IonText,
} from '@ionic/react';
import { alertCircleOutline, refreshOutline, homeOutline } from 'ionicons/icons';
import {ErrorBoundaryProps} from "../modules/components/interfaces/ErrorBoundaryProps";
import {ErrorBoundaryState} from "../modules/components/interfaces/ErrorBoundaryState";
import styles from "./ErrorBoundary.module.css";
import { telemetryService } from '../services/offline/telemetry/telemetry-service';
import { debugError } from '../utils/debug';

/**
 * Error Boundary per catturare errori React e mostrare UI di fallback
 * Avvolge componenti per prevenire crash dell'intera app
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        debugError('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({ error, errorInfo });

        telemetryService.trackError(error, {
            source: 'ErrorBoundary',
            componentStack: errorInfo.componentStack ?? undefined,
        });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    handleGoHome = () => {
        this.handleReset();
        // Use replace() to avoid adding a broken entry to the history stack.
        window.location.replace('/');
    };

    render() {
        if (this.state.hasError) {
            // Se è stato fornito un fallback custom, usalo
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Altrimenti mostra UI di errore di default
            return (
                <IonPage>
                    <IonContent className="ion-padding">
                        <div className={`content-container ${styles.container}`}>
                            <IonIcon
                                icon={alertCircleOutline}
                                className={styles.icon}
                            />

                            <IonText>
                                <h2>Oops! Qualcosa è andato storto</h2>
                                <p className={styles.message}>
                                    Si è verificato un errore imprevisto.
                                </p>
                            </IonText>

                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <div className={styles.devError}>
                                    <IonText color="danger">
                                        <strong>Error:</strong> {this.state.error.toString()}
                                    </IonText>
                                    {this.state.errorInfo && (
                                        <pre className={styles.stack}>
                                            {this.state.errorInfo.componentStack}
                                        </pre>
                                    )}
                                </div>
                            )}

                            <div className={styles.actions}>
                                <IonButton onClick={this.handleReset} color="primary">
                                    <IonIcon slot="start" icon={refreshOutline} />
                                    Riprova
                                </IonButton>
                                <IonButton onClick={this.handleGoHome} color="secondary" fill="outline">
                                    <IonIcon slot="start" icon={homeOutline} />
                                    Torna alla Home
                                </IonButton>
                            </div>
                        </div>
                    </IonContent>
                </IonPage>
            );
        }

        return this.props.children;
    }
}
